import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

// Read shape of one Distribution List Definition grid row
// (view__distribution_lists): a public.distribution_lists row plus its topic
// label and its aggregated three filter join tables (roles / funds /
// lp_statuses) as id arrays + display labels + counts.
export const DistributionListGridModelSchema = z
  .object({
    distribution_lists__id: z.string(),
    distribution_lists__name: z.string(),
    distribution_lists__topic_id: z.string(),
    distribution_lists__topic_label: z.string(),
    distribution_lists__role_ids: z.array(z.string()),
    distribution_lists__role_labels: z.string(),
    distribution_lists__role_count: z.number(),
    distribution_lists__fund_ids: z.array(z.string()),
    distribution_lists__fund_labels: z.string(),
    distribution_lists__fund_count: z.number(),
    distribution_lists__lp_status_ids: z.array(z.string()),
    distribution_lists__lp_status_labels: z.string(),
    distribution_lists__lp_status_count: z.number(),
    distribution_lists__account_ids: z.array(z.string()),
    distribution_lists__account_labels: z.string(),
    distribution_lists__account_count: z.number(),
    distribution_lists__contact_ids: z.array(z.string()),
    distribution_lists__contact_labels: z.string(),
    distribution_lists__contact_count: z.number(),
    distribution_lists__date_modified: z.string()
  })
  .strict()

export const DistributionListGridModelFields = makeFieldKeys(DistributionListGridModelSchema)

export type DistributionListGridModel = z.infer<typeof DistributionListGridModelSchema>

// Definition modal filter-options (REUSES the Phase 0
// view__distribution_list_filter_options() — decision §2.12b): the
// {value,label} option universes for the topic (single) / role / fund /
// LP-status (multi) selects.
// `email` is OPTIONAL and only carried by `contacts` entries (§2.15f —
// disambiguates duplicate-named contacts; the contact picker renders it as
// smaller secondary text). The other five option lists omit it; a contact
// with no email at all carries null. Additive / backward-compatible.
export const DistributionListFilterOptionSchema = z
  .object({
    value: z.string(),
    label: z.string(),
    email: z.string().nullable().optional()
  })
  .strict()

export const DistributionListFilterOptionsModelSchema = z
  .object({
    topic: z.array(DistributionListFilterOptionSchema),
    roles: z.array(DistributionListFilterOptionSchema),
    funds: z.array(DistributionListFilterOptionSchema),
    lp_status: z.array(DistributionListFilterOptionSchema),
    accounts: z.array(DistributionListFilterOptionSchema),
    contacts: z.array(DistributionListFilterOptionSchema)
  })
  .strict()

export type DistributionListFilterOption = z.infer<typeof DistributionListFilterOptionSchema>
export type DistributionListFilterOptionsModel = z.infer<typeof DistributionListFilterOptionsModelSchema>

export function validateDistributionListGridModel(data: unknown): DistributionListGridModel {
  try {
    return validateWithZodSchema(DistributionListGridModelSchema, data, 'distribution list grid model')
  } catch (error) {
    throw new Error(`Distribution list grid model validation failed: ${error}`)
  }
}

export function validateDistributionListFilterOptionsModel(
  data: unknown
): DistributionListFilterOptionsModel {
  try {
    return validateWithZodSchema(
      DistributionListFilterOptionsModelSchema,
      data,
      'distribution list filter options model'
    )
  } catch (error) {
    throw new Error(`Distribution list filter options model validation failed: ${error}`)
  }
}
